import { ProcessingJobType } from "@prisma/client";

// The single source of truth for every price. Credits are sold as a batch of UNIT_SIZE at
// UNIT_PRICE_EUR, and every paid action is priced here, in credits. See docs/Pricing.md for
// the grid and the margins.
export const Credits = {
  UNIT_SIZE: 100,
  UNIT_PRICE_EUR: 39,

  USD_TO_EUR: 0.92,
  MIN_UNITS: 1,
  MAX_UNITS: 10,

  DEFAULT_FREE_CREDITS: 30,

  // Metered by time: a project owes this many credits per started hour of analyzed audio.
  ANALYSIS_CREDITS_PER_HOUR: 10,

  // Metered by volume: how many units one credit buys.
  PROSPECTS_PER_CREDIT: 2,
  IMPORTED_PROFILES_PER_CREDIT: 100,
  DEEP_IMPORTED_PROFILES_PER_CREDIT: 10,
  POOL_PROFILES_PER_CREDIT: 100,
  EXTENSION_PROFILES_PER_CREDIT: 1,
  MCP_CALLS_PER_CREDIT: 100,

  AUTO_RECHARGE_THRESHOLDS: [10, 25, 50, 100] as const,
  AUTO_RECHARGE_DEFAULT_THRESHOLD: 25,
  AUTO_RECHARGE_DEFAULT_UNITS: 1,
  AUTO_RECHARGE_DEFAULT_MONTHLY_MAX_UNITS: 5,
  AUTO_RECHARGE_MAX_MONTHLY_MAX_UNITS: 20,
  AUTO_RECHARGE_MAX_FAILURES: 2,
  AUTO_RECHARGE_PENDING_TIMEOUT_MINUTES: 15,
  AUTO_RECHARGE_PERIOD_DAYS: 30,
} as const;

// Flat per-job price, keyed by the job type so the charge and the failure refund both read it.
// CALL_ANALYSIS and IMPORTED_PROFILE_DETECTION are 0 here because they are metered elsewhere
// (per hour, and up-front by the detection flow).
export const ProcessingPrice: Record<ProcessingJobType, number> = {
  [ProcessingJobType.CALL_ANALYSIS]: 0,
  [ProcessingJobType.CLIENT_AVATAR]: 10,
  [ProcessingJobType.PROSPECT_SEARCH]: 5,
  [ProcessingJobType.IMPORTED_PROFILE_DETECTION]: 0,
  [ProcessingJobType.MESSAGE_SEQUENCE]: 1,
  [ProcessingJobType.MESSAGE_SEQUENCE_STEP]: 1,
  [ProcessingJobType.REPLY_SUGGESTION]: 1,
  [ProcessingJobType.OFFER_EXTRACTION]: 5,
  [ProcessingJobType.CALL_INSIGHT_RECOUNT]: 1,
};

export function analysisSecondsForCredits(credits: number): number {
  return (Math.max(0, credits) / Credits.ANALYSIS_CREDITS_PER_HOUR) * 3600;
}

function hourCredits(seconds: number): number {
  return Math.ceil(Math.max(0, seconds) / 3600) * Credits.ANALYSIS_CREDITS_PER_HOUR;
}

// A sequence generation is billed per variant: one credit for each arm written across every
// step, so 1 -> 1, 2 -> 2, 3 -> 3. Same unit as filling a prospect's messages, one per arm.
export function poolRevealCost(quantity: number, prepaid: number): number {
  return Math.ceil(Math.max(0, quantity - prepaid) / Credits.POOL_PROFILES_PER_CREDIT);
}

export function poolRevealLeftover(quantity: number, prepaid: number): number {
  return prepaid + poolRevealCost(quantity, prepaid) * Credits.POOL_PROFILES_PER_CREDIT - quantity;
}

export function sequenceGenerationCost(perVariant: number, variantsCount: number): number {
  return perVariant * Math.max(1, variantsCount);
}

// Call analysis is billed per project on a cumulative basis: a project owes ANALYSIS_CREDITS_PER_HOUR
// credits per started hour of analyzed audio, summed across all its analyzed calls. Analyzing a new
// call costs for the number of hour boundaries its duration makes the project total cross, so a short
// call that fits inside the current started hour is free (0 credit).
export function creditsForAddedDuration(
  billedSeconds: number | null | undefined,
  addedSeconds: number | null | undefined,
): number {
  const billed = billedSeconds ?? 0;
  return hourCredits(billed + (addedSeconds ?? 0)) - hourCredits(billed);
}

export function creditsForRemovedDuration(
  billedSeconds: number | null | undefined,
  removedSeconds: number | null | undefined,
): number {
  const billed = billedSeconds ?? 0;
  return hourCredits(billed) - hourCredits(billed - (removedSeconds ?? 0));
}
